import { createFileRoute } from "@tanstack/react-router";
import { Reveal } from "@/components/site/Reveal";
import { CtaPanel, PageHero } from "@/components/site/Sections";
import { processSteps as processStepsData } from "@/lib/finendra-data";
import { useCmsList } from "@/hooks/use-cms";

const URL = "https://finendra.com/how-it-works";
const OG = "https://finendra.com/og/og-default.png";

const timeline = [
  {
    week: "Day 1–3",
    title: "Discovery call and scope",
    body: "A 30-minute call to understand your entity structure, transaction volume, systems, and reporting needs. You receive a written scope and a fixed monthly fee.",
  },
  {
    week: "Week 1",
    title: "Access, security, and handover",
    body: "We set up least-privilege access to your accounting stack, document current workflows, and agree on a monthly close calendar with named owners.",
  },
  {
    week: "Week 2–4",
    title: "Clean-up and first close",
    body: "Historical periods are reconciled, the chart of accounts is tidied, and we run your first monthly close under the new process.",
  },
  {
    week: "Ongoing",
    title: "Steady-state operations",
    body: "Your pod runs bookkeeping, payroll, AP, AR, and reporting on a fixed calendar, with a monthly review call and quarterly scope check-in.",
  },
];

const deliverables = [
  "Reconciled bank, card, and merchant accounts",
  "Monthly P&L, balance sheet, and cash-flow statement",
  "AP aging, AR aging, and collections status",
  "Payroll journals and benefit accruals posted",
  "Variance notes on anything unusual in the period",
  "A named point of contact who knows your business",
];

export const Route = createFileRoute("/how-it-works")({
  head: () => ({
    meta: [
      { title: "How It Works | Finendra Onboarding & Monthly Close" },
      {
        name: "description",
        content:
          "From discovery call to steady-state operations in about four weeks. See exactly how Finendra onboards your books, runs the monthly close, and reports back.",
      },
      { property: "og:title", content: "How Finendra Works" },
      {
        property: "og:description",
        content:
          "Discovery, onboarding, clean-up, and a fixed monthly close calendar — the full engagement process.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: URL },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:image", content: OG },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { name: "twitter:image", content: OG },
    ],
    links: [{ rel: "canonical", href: URL }],
  }),
  component: HowItWorksPage,
});

function HowItWorksPage() {
  const processSteps = useCmsList("process", processStepsData);

  return (
    <>
      <PageHero
        eyebrow="How it works"
        title="From messy books to a predictable monthly close."
        body="A structured onboarding, a documented process, and a fixed calendar — so you always know what is happening with your finances and when."
      />

      <section className="px-6 pb-24">
        <div className="mx-auto grid max-w-7xl gap-8 md:grid-cols-3">
          {processSteps.map((s, i) => (
            <Reveal
              key={s.step}
              delay={i * 0.08}
              className="rounded-3xl border border-brand-navy/10 bg-brand-surface p-8"
            >
              <span className="font-display text-4xl font-extrabold text-brand-accent">
                {s.step}
              </span>
              <h2 className="mt-5 font-display text-xl font-bold text-brand-navy">{s.title}</h2>
              <p className="mt-3 text-brand-navy/60">{s.body}</p>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="bg-brand-navy px-6 py-24">
        <div className="mx-auto max-w-4xl">
          <Reveal>
            <p className="text-xs font-bold uppercase tracking-widest text-brand-accent">
              Engagement timeline
            </p>
            <h2 className="mt-5 font-display text-4xl font-bold text-background md:text-5xl">
              What the first month looks like
            </h2>
          </Reveal>

          <ol className="mt-14 space-y-10 border-l border-background/15 pl-8">
            {timeline.map((t, i) => (
              <Reveal key={t.week} delay={i * 0.06} className="relative">
                <span className="absolute -left-[41px] mt-2 size-3 rounded-full bg-brand-accent" />
                <p className="text-xs font-bold uppercase tracking-widest text-brand-accent">
                  {t.week}
                </p>
                <h3 className="mt-2 font-display text-2xl font-bold text-background">{t.title}</h3>
                <p className="mt-2 leading-relaxed text-background/60">{t.body}</p>
              </Reveal>
            ))}
          </ol>
        </div>
      </section>

      <section className="px-6 py-24">
        <div className="mx-auto max-w-4xl">
          <Reveal>
            <h2 className="font-display text-4xl font-bold text-brand-navy">
              What you receive every month
            </h2>
          </Reveal>
          <ul className="mt-10 grid gap-4 sm:grid-cols-2">
            {deliverables.map((d, i) => (
              <Reveal
                key={d}
                delay={i * 0.04}
                className="rounded-2xl border border-brand-navy/10 bg-brand-surface px-6 py-5 text-brand-navy/70"
              >
                {d}
              </Reveal>
            ))}
          </ul>
        </div>
      </section>

      <CtaPanel
        title="See the process applied to your books."
        body="Bring your current setup to a discovery call and we will map the exact onboarding plan for your business."
      />
    </>
  );
}
